import * as React from "react";
import { useState, useContext } from "react";
import { useTheme } from "@mui/material/styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import {
  OutlinedInput,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Button,
  TextField,
  Card,
  IconButton,
  CardHeader,
  CardContent,
  Box,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import Grid2 from "@mui/material/Grid2";
import { PhotoCamera, Delete } from "@mui/icons-material";
import {
  Prefixes,
  Genders,
  BloodGroups,
  NationalIdTypes,
  BloodGroupOptions,
} from "../Utility/Constants";
import { updatePatient } from "../API/PatientsApi";
import AuthContext from "../Context/AuthProvider";
import { useSnackbar } from "../Utility/UserMessageContext";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { calculateAge, showLoadingImage } from "../Utility/Utility";

const maritalStatusOptions = ["Single", "Married", "Divorced", "Widowed"];
const genderOptions = ["Male", "Female", "Other"];
const prefixOptions = ["Mr.", "Ms.", "Dr.", "Prof."];

function EditPatient({ patient, onSave }) {
  const theme = useTheme();
  const { auth } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  const [editedPatient, setEditedPatient] = useState(patient);
  const [ageInYears, setAgeInYears] = useState();
  const [isLoading, setIsLoading] = useState(false);
  
  // Single state variable for loading/saving state
  const [isSaving, setIsSaving] = useState(false);
  
  // Form submission state
  const [isSubmitted, setIsSubmitted] = useState(false);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  function getStyles(option, selectedOptions, theme) {
    return {
      fontWeight: selectedOptions.includes(option)
        ? theme.typography.fontWeightMedium
        : theme.typography.fontWeightRegular,
    };
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedPatient((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDoBChange = (value) => {
    setEditedPatient((prev) => ({
      ...prev,
      ["dateOfBirth"]: value,
    }));
    setAgeInYears(calculateAge(value));
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileSizeLimit = 1 * 1024 * 1024; // 1MB
    const allowedTypes = ["image/jpeg", "image/png", "image/gif"];

    if (!allowedTypes.includes(file.type)) {
      alert("Only JPG, PNG, and GIF files are allowed.");
      return;
    }

    if (file.size > fileSizeLimit) {
      alert("File size must be less than 1MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setEditedPatient((prev) => ({
        ...prev,
        photo: e.target.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoDelete = () => {
    setEditedPatient((prev) => ({
      ...prev,
      photo: "",
    }));
  };

  const handleSave = async () => {
    const requiredFields = [
      {
        field: "firstName",
        message: "Filling in all mandatory fields is required",
      },
      {
        field: "prefix",
        message: "Filling in all mandatory fields is required",
      },
      { 
        field: "dateOfBirth", 
        message: "Filling in all mandatory fields is required" 
      },
      {
        field: "mobileNo",
        message: "Filling in all mandatory fields is required",
      },
      {
        field: "gender",
        message: "Filling in all mandatory fields is required",
      },
      {
        field: "lastName",
        message: "Filling in all mandatory fields is required",
      },
    ];
    setIsSubmitted(true); // Set form as submitted

    // Check for missing required fields
    for (const { field, message } of requiredFields) {
      if (!editedPatient[field]) {
        showSnackbar(message, "error");
        return; // Stop execution if any required field is missing
      }
    }
    
    // Set saving state to true before API call
    setIsSaving(true);
    
    try {
      // Call the updatePatient function to save changes
      const result = await updatePatient(auth, editedPatient.id, editedPatient);

      if (result.isSuccess) {
        showSnackbar("Patient details updated successfully.", "success");
        onSave(editedPatient);
      } else {
        showSnackbar("Failed to update patient details.", "error");
      }
    } catch (error) {
      showSnackbar(
        "An error occurred while updating patient details.",
        "error"
      );
    } finally {
      // Reset saving state when operation completes
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ position: 'relative' }}> {/* Wrapper Box with relative position */}
              {/* Loading Overlay for General Note content only */}
                {isSaving && (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(216, 214, 214, 0.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1301, // One level above Dialog
      }}
    >
      {showLoadingImage()}
    </div>
  )}
    <Grid2
      size={12}
      container
      direction="column"
      sx={{ padding: 2 }}
      spacing={1}
    >
      <Grid2 size={4}>
        <Button variant="contained" color="secondary" onClick={handleSave} disabled={isLoading || isSaving} >
          Save
        </Button>
      </Grid2>
      <Grid2 size={12} container spacing={1} sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto"}}>
        <Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}} container spacing={2}>
          <Grid2>
            <Card variant="outlined" sx={{ padding: "10px", width: "100%",pointerEvents: isSaving || isLoading ? "none" : "auto",
    opacity: isSaving || isLoading ? 0.6 : 1,}}>
              <CardHeader
                subheader="Patient's Details"
                sx={{ paddingX: 0 }}
              ></CardHeader>
              <Grid2 container spacing={2}>
                {/* Prefix, First Name, Last Name */}
                <Grid2 size={12} container alignItems="center" spacing={2}>
                  <Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                    <FormControl
                      fullWidth
                      margin="dense"
                      size="small"
                      required
                      error={isSubmitted && !editedPatient.prefix} 
                      sx={{
                        "& .MuiFormLabel-asterisk": {
                          color: "red", 
                        },
                      }}
                    >
                      <InputLabel id="prefix-label">Prefix</InputLabel>
                      <Select
                        labelId="prefix-label"
                        name="prefix"
                        value={editedPatient.prefix || ""}
                        onChange={handleChange}
                        input={<OutlinedInput label="Prefix" />}
                        MenuProps={MenuProps}
                      >
                        {Prefixes.map((option) => (
                          <MenuItem
                            key={option}
                            value={option}
                            style={getStyles(
                              option,
                              [editedPatient.prefix],
                              theme
                            )}
                          >
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid2>
                  <Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                    <TextField
                      margin="dense"
                      size="small"
                      variant="outlined"
                      label="Suffix"
                      name="suffix"
                      fullWidth
                      value={editedPatient.suffix || ""}
                      onChange={handleChange}
                    />
                  </Grid2>
                </Grid2>
                <Grid2 size={12} container alignItems="center" spacing={2}>
                  <Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                    <TextField
                      margin="dense"
                      size="small"
                      variant="outlined"
                      label="First Name"
                      name="firstName"
                      fullWidth
                      value={editedPatient.firstName || ""}
                      onChange={handleChange}
                      required
                      error={isSubmitted && !editedPatient.firstName} 
                      sx={{
                        "& .MuiFormLabel-asterisk": {
                          color: "red", 
                        },
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                    <TextField
                      margin="dense"
                      size="small"
                      variant="outlined"
                      label="Last Name"
                      name="lastName"
                      fullWidth
                      value={editedPatient.lastName || ""}
                      onChange={handleChange}
                      required
                      error={isSubmitted && !editedPatient.lastName} 
                      sx={{
                        "& .MuiFormLabel-asterisk": {
                          color: "red", 
                        },
                      }}
                    />
                  </Grid2>
                </Grid2>

                <Grid2 container size={12} spacing={2} direction="column">
                  {/* Row for Grid 1 and Grid 2 */}
                  <Grid2 container size={12} spacing={2}>
                    
                    {/* Grid 2 - Date of Birth */}
                    <Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Date of Birth"
                          maxDate={dayjs()}
                          name="dateOfBirth"
                          fullWidth
                          required
                          slotProps={{ textField: { size: 'small', margin: "dense"} }}
                          value={dayjs(editedPatient?.dateOfBirth)}
                        onChange={handleDoBChange}
                        error={isSubmitted && !editedPatient?.dateOfBirth} // Show error only after submission
                        sx={{
                          "& .MuiFormLabel-asterisk": {
                            color: "red", // Make the asterisk red
                          },
                          width: "100%",
                        }}
                          
                        />
                      </LocalizationProvider>
                    </Grid2>

                    {/* Grid 1 - Age */}
                    <Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                      <TextField
                        margin="dense"
                        size="small"
                        label="Age"
                        name="age"
                        fullWidth
                        inputProps={{ readOnly: true }}
                        value={calculateAge(editedPatient?.dateOfBirth)}
                        slotProps={{
                          inputLabel: { shrink: true },
                        }}
                      />
                    </Grid2>
                  </Grid2>

                  {/* Row for Grid 3 and Grid 4 */}
                  <Grid2 container size={12}>
                    {/* Grid 3 - Email */}
                    <Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                      <TextField
                        margin="dense"
                        size="small"
                        label="Email"
                        name="emailId"
                        fullWidth
                        value={editedPatient.emailId}
                        onChange={handleChange}
                      />
                    </Grid2>

                    {/* Grid 4 - Phone */}
                    <Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                      <TextField
                        margin="dense"
                        size="small"
                        label="Phone"
                        name="mobileNo"
                        fullWidth
                        value={editedPatient.mobileNo || ""}
                        onChange={handleChange}
                        required // Make the field required
                        error={isSubmitted && !editedPatient.mobileNo} // Show error if the field is empty
                        //helperText={!patientDetails.phone ? "Phone is required" : ""} // Error message
                        sx={{
                          "& .MuiFormLabel-asterisk": {
                            color: "red", // Make the asterisk red
                          },
                        }}
                      />
                    </Grid2>
                  </Grid2>
                </Grid2>

                {/* Other Details */}
                <Grid2 size={12} container alignItems="center" spacing={2}>
                  <Grid2 size={{xs:12, sm:12, md:12, lg:4, xl:4}}>
                    <FormControl fullWidth margin="dense" size="small">
                      <InputLabel id="marital-status-label">
                        Marital Status
                      </InputLabel>
                      <Select
                        labelId="marital-status-label"
                        name="maritalStatus"
                        value={editedPatient.maritalStatus}
                        onChange={handleChange}
                        input={<OutlinedInput label="Marital Status" />}
                        MenuProps={MenuProps}
                      >
                        {maritalStatusOptions.map((option) => (
                          <MenuItem
                            key={option}
                            value={option}
                            style={getStyles(
                              option,
                              [editedPatient.maritalStatus],
                              theme
                            )}
                          >
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid2>

                  <Grid2 size={{xs:12, sm:12, md:12, lg:4, xl:4}}>
                    <FormControl fullWidth margin="dense" size="small">
                      <InputLabel id="blood-group-label">
                        Blood Group
                      </InputLabel>
                      <Select
                        labelId="blood-group-label"
                        name="bloodGroup"
                        value={editedPatient.bloodGroup}
                        onChange={handleChange}
                        input={<OutlinedInput label="Blood Group" />}
                        MenuProps={MenuProps}
                      >
                        {BloodGroupOptions.map((option) => (
                          <MenuItem
                            key={option.value}
                            value={option.value}
                            style={getStyles(
                              option,
                              [editedPatient.bloodGroup],
                              theme
                            )}
                          >
                            {option.displayValue}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid2>

                  <Grid2 size={{xs:12, sm:12, md:12, lg:4, xl:4}}>
                    <FormControl fullWidth margin="dense" size="small">
                      <InputLabel id="gender-label">Gender</InputLabel>
                      <Select
                        labelId="gender-label"
                        name="gender"
                        value={editedPatient.gender}
                        onChange={handleChange}
                        input={<OutlinedInput label="Gender" />}
                        MenuProps={MenuProps}
                      >
                        {Genders.map((option) => (
                          <MenuItem
                            key={option}
                            value={option}
                            style={getStyles(
                              option,
                              [editedPatient.gender],
                              theme
                            )}
                          >
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid2>
                </Grid2>
              </Grid2>
            </Card>
          </Grid2>

          {/* Address Details Section */}
          <Grid2>
            <Card variant="outlined" sx={{ padding: "10px", width: "100%",pointerEvents: isSaving || isLoading ? "none" : "auto",
    opacity: isSaving || isLoading ? 0.6 : 1, }}>
              <CardHeader
                subheader="Address Details"
                sx={{ paddingX: 0 }}
              ></CardHeader>
              <Grid2 container spacing={2}>
                <Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                  <TextField
                    margin="dense"
                    size="small"
                    label="House No"
                    name="addressHouseNo"
                    fullWidth
                    value={editedPatient.addressHouseNo}
                    onChange={handleChange}
                  />
                </Grid2>
                <Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                  <TextField
                    margin="dense"
                    size="small"
                    label="Street"
                    name="addressStreet"
                    fullWidth
                    value={editedPatient.addressStreet}
                    onChange={handleChange}
                  />
                </Grid2>
                <Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                  <TextField
                    margin="dense"
                    size="small"
                    label="City"
                    name="addressCity"
                    fullWidth
                    value={editedPatient.addressCity}
                    onChange={handleChange}
                  />
                </Grid2>
                <Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                  <TextField
                    margin="dense"
                    size="small"
                    label="State"
                    name="addressState"
                    fullWidth
                    value={editedPatient.addressState}
                    onChange={handleChange}
                  />
                </Grid2>
                <Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                  <TextField
                    margin="dense"
                    size="small"
                    label="Country"
                    name="addressCountry"
                    fullWidth
                    value={editedPatient.addressCountry}
                    onChange={handleChange}
                  />
                </Grid2>
                <Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                  <TextField
                    margin="dense"
                    size="small"
                    label="Pin Code"
                    name="addressPIN"
                    fullWidth
                    value={editedPatient.addressPIN}
                    onChange={handleChange}
                  />
                </Grid2>
              </Grid2>
            </Card>
          </Grid2>
          {/* National ID Proof Section */}
          <Grid2 size={12}>
            <Card variant="outlined" sx={{ padding: "10px", width: "100%", pointerEvents: isSaving || isLoading ? "none" : "auto",
    opacity: isSaving || isLoading ? 0.6 : 1, }}>
              <CardHeader
                subheader="National ID "
                sx={{ paddingX: 0 }}
              ></CardHeader>
              <Grid2 container spacing={2}>
                <Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                  <FormControl fullWidth margin="dense" size="small">
                    <InputLabel id="national-id-proof-label">
                      National ID Type
                    </InputLabel>
                    <Select
                      labelId="national-id-proof-label"
                      name="nationalIdType"
                      value={editedPatient.nationalIdType}
                      onChange={handleChange}
                      input={<OutlinedInput label="National ID Type" />}
                    >
                      {NationalIdTypes.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid2>

                <Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                  <TextField
                    margin="dense"
                    size="small"
                    label="ID Number"
                    name="nationalId"
                    fullWidth
                    value={editedPatient.nationalId}
                    onChange={handleChange}
                    slot={{ inputLabel: { shrink: editedPatient.nationalId } }}
                  />
                </Grid2>
              </Grid2>
            </Card>
          </Grid2>

          {/* Emergency Contact Details Section */}
          <Grid2 size={12}>
            <Card variant="outlined" sx={{ padding: "10px", width: "100%", pointerEvents: isSaving || isLoading ? "none" : "auto",
    opacity: isSaving || isLoading ? 0.6 : 1, }}>
              <CardHeader
                subheader="Emergency Contact Details"
                sx={{ paddingX: 0 }}
              ></CardHeader>
              <Grid2 container spacing={2}>
                <Grid2 size={{xs:12, sm:12, md:12, lg:4, xl:4}}>
                  <TextField
                    margin="dense"
                    size="small"
                    variant="outlined"
                    label="Name"
                    name="emergencyContactPerson"
                    fullWidth
                    value={editedPatient.emergencyContactPerson}
                    onChange={handleChange}
                  />
                </Grid2>
                <Grid2 size={{xs:12, sm:12, md:12, lg:4, xl:4}}>
                  <TextField
                    margin="dense"
                    size="small"
                    label="Phone"
                    name="emergencyContactPhoneNumber"
                    fullWidth
                    value={editedPatient.emergencyContactPhoneNumber}
                    onChange={handleChange}
                  />
                </Grid2>
                <Grid2 size={{xs:12, sm:12, md:12, lg:4, xl:4}}>
                  <TextField
                    margin="dense"
                    size="small"
                    label="Relationship"
                    name="emergencyContactRelationship"
                    fullWidth
                    value={editedPatient.emergencyContactRelationship}
                    onChange={handleChange}
                  />
                </Grid2>
              </Grid2>
            </Card>
          </Grid2>
        </Grid2>

        {/* Image Upload Section */}
        <Grid2
          size={{xs:8, sm:8, md:8, lg:2, xl:2}}
          container
          alignItems="flex-start"
          justifyContent="flex-start"
          direction="column"
        >
          <Card variant="outlined" sx={{ padding: "10px", width: "100%", pointerEvents: isSaving || isLoading ? "none" : "auto",
    opacity: isSaving || isLoading ? 0.6 : 1, }}>
            <CardHeader subheader="Upload Image" sx={{ paddingX: 0 }} />
            <CardContent>
              <Grid2
                container
                alignItems="center"
                justifyContent="center"
                spacing={2}
              >
                {/* <Grid2 size={12} container justifyContent="center">
                  <img
                    src={
                      editedPatient.photo ||
                      "./placeholder-image-male-gender.webp"
                    }
                    alt="PatientPhoto"
                    width="120"
                    height="120"
                    className="border rounded p-0"
                  />
                </Grid2>
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  id="photo-upload"
                  onChange={handlePhotoUpload}
                />
                <label htmlFor="photo-upload">
                  <IconButton component="span">
                    <PhotoCamera />
                  </IconButton>
                </label>
                {editedPatient.photo && (
                  <IconButton onClick={handlePhotoDelete}>
                    <Delete />
                  </IconButton>
                )} */}

                <Grid2 size={12} container justifyContent="center">
                  <img
                    src={
                      editedPatient.photo ||
                      "./placeholder-image-male-gender.webp"
                    }
                    alt="PatientPhoto"
                    width="120"
                    height="120"
                    className="border rounded p-0"
                  />
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ padding: 0 }}
                  >
                    Upload image size max 1MB
                  </Typography>
                </Grid2>

                {/* File Input (Hidden) */}
                <input
                  type="file"
                  accept="image/jpeg, image/png, image/gif"
                  hidden
                  id="photo-upload"
                  onChange={handlePhotoUpload}
                />

                <Grid2
                  container
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                >
                  {/* Upload Button */}
                  <Grid2>
                    <label htmlFor="photo-upload">
                      <IconButton component="span">
                        <PhotoCamera />
                      </IconButton>
                    </label>
                  </Grid2>

                  {editedPatient.photo && (
                    <Grid2>
                      <IconButton onClick={handlePhotoDelete}>
                        <Delete />
                      </IconButton>
                    </Grid2>
                  )}
                </Grid2>
              </Grid2>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>
    </Grid2>
    </Box>
  );
}

export default EditPatient;
